/**
 * Route guard. Renders its children only for a signed-in user.
 *
 * This is a usability boundary, not a security one — every `/api/v1` route is
 * enforced server-side by `get_current_user`. Its job is to keep an anonymous
 * visitor from staring at an empty dashboard that 401s on every request.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'

import { useI18n } from '../i18n'
import { useAuth } from './useAuth'

export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useAuth()
  const location = useLocation()

  // `loading` must render neither branch. Redirecting here would bounce an
  // authenticated user to /login on every reload, because the first paint
  // always happens before /auth/me answers.
  if (status === 'loading') return <AuthPending />

  if (status === 'anonymous') {
    // `from` lets the login screen return the user to the page they asked for
    // instead of dumping everyone on the overview.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  return <>{children ?? <Outlet />}</>
}

function AuthPending() {
  const { t } = useI18n()
  return (
    <div
      className="flex h-full items-center justify-center text-sm text-muted"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">{t('auth.requireAuth.checkingSession')}</span>
      <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-accent" />
    </div>
  )
}
